import React, { useState, useEffect, useCallback, useRef } from 'react';
import TeamworkButton from './TeamworkButton';
import styled, { keyframes, css } from 'styled-components';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import { getImageURL } from '../utils/api';
import NotificationService from '../services/notificationService';
import TeamworkLiveToast from './TeamworkLiveToast';
import { prepareBrowserNotifications, showTeamworkBrowserNotification } from '../utils/browserNotifications';
import { subscribePush } from '../utils/webPushClient';

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  height: 64px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb; /* gray-200 */
  background: #ffffff;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  color: #4b5563; /* gray-600 */
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #f3f4f6; color: #111827; }
`;

const Input = styled.input`
  width: 256px;
  border: 1px solid #d1d5db; /* gray-300 */
  background: #f9fafb;
  border-radius: 8px;
  padding: 8px 12px 8px 36px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #6366f1; box-shadow: 0 0 0 2px rgba(99,102,241,0.2); }
`;

const Dropdown = styled.div`
  position: absolute;
  right: 0;
  margin-top: 8px;
  width: 224px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1);
`;

const Row = styled.div`
  padding: 10px 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const MenuBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  &:hover { background: #f3f4f6; }
`;

const RoleBadge = styled.span`
  display: inline-block;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #d1d5db;
`;

const NotifDropdown = styled.div`
  position: absolute;
  right: 0;
  margin-top: 8px;
  width: 320px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 10px 25px -5px rgba(0,0,0,0.12), 0 4px 10px -5px rgba(0,0,0,0.08);
  z-index: 50;
  overflow: hidden;
`;

const NotifBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  border-radius: 9999px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
  pointer-events: none;
`;

const NotifItem = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  background: ${(p) => (p.$unread ? '#f5f3ff' : 'transparent')};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
  &:hover { background: #f3f4f6; }
  &:last-of-type { border-bottom: none; }
`;

const _presenceLivePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55); }
  60%       { box-shadow: 0 0 0 5px rgba(34, 197, 94, 0); }
`;

const PresenceLiveDot = styled.span`
  position: absolute;
  top: 4px;
  left: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  border: 1.5px solid #ffffff;
  animation: ${_presenceLivePulse} 1.8s ease-in-out infinite;
  pointer-events: none;
`;

const PresenceCountBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 17px;
  height: 17px;
  border-radius: 9999px;
  background: #22c55e;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
  pointer-events: none;
  border: 1.5px solid #ffffff;
`;

const _presencePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18); }
  50%       { box-shadow: 0 0 0 7px rgba(34, 197, 94, 0.30); }
`;

const PresenceDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  flex-shrink: 0;
  background: ${(p) => (p.$online ? '#22c55e' : '#cbd5e1')};
  box-shadow: 0 0 0 3px ${(p) => (p.$online ? 'rgba(34,197,94,0.16)' : 'rgba(203,213,225,0.28)')};
  ${(p) => p.$online && css`animation: ${_presencePulse} 2s ease-in-out infinite;`}
`;

const PresenceModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.40);
  backdrop-filter: blur(2px);
  z-index: 70;
`;

const PresenceModal = styled.div`
  position: fixed;
  top: 72px;
  right: 20px;
  width: min(440px, calc(100vw - 24px));
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(30, 58, 138, 0.14);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 32px 80px rgba(15, 23, 42, 0.28), 0 8px 24px rgba(15, 23, 42, 0.12);
  z-index: 71;
  overflow: hidden;
`;

const PresenceModalHead = styled.div`
  padding: 0;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(160deg, #0f2b5b 0%, #1e4a9e 60%, #1d4ed8 100%);
`;

const PresenceModalHeadTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
`;

const PresenceModalHeadStats = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border-top: 1px solid rgba(255,255,255,0.10);
`;

const PresenceStatPill = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px 0;
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.$color || 'rgba(255,255,255,0.75)'};
  border-right: 1px solid rgba(255,255,255,0.10);
  &:last-child { border-right: none; }
`;

const PresenceSearchStrip = styled.div`
  padding: 10px 16px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
`;

const PresenceModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f8fafc;
`;

const PresenceSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PresenceSectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 11px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 0 2px 6px;
  border-bottom: 1px solid #e2e8f0;
`;

const PresenceSectionCount = styled.span`
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0369a1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
`;

const PresenceEmpty = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 18px 16px;
  background: #ffffff;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
`;

const PresenceMemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid ${(p) => (p.$online ? '#d1fae5' : '#e2e8f0')};
  border-radius: 12px;
  background: #ffffff;
  transition: box-shadow 0.15s, border-color 0.15s;
  &:hover {
    border-color: ${(p) => (p.$online ? '#6ee7b7' : '#cbd5e1')};
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.07);
  }
`;

const PresenceAvatar = styled.div`
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PresenceInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PresenceName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`;

const PresenceRole = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const PresenceHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PresenceHeadButton = styled.button`
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.90);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: background 0.15s, border-color 0.15s;
  &:hover { background: rgba(255,255,255,0.22); border-color: rgba(255,255,255,0.40); }
`;

const PresenceSearchWrap = styled.div`
  padding: 0;
`;

const PresenceSearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  background: #ffffff;
  padding: 8px 12px 8px 34px;
  font-size: 13px;
  color: #0f172a;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14);
  }
  &::placeholder { color: #94a3b8; }
`;

const _liveDotBlink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.35; }
`;

const PresenceLiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  color: #15803d;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #22c55e;
    animation: ${_liveDotBlink} 1.4s ease-in-out infinite;
  }
`;

/** Format an ISO timestamp as a relative time string. */
function relativeTime(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
}

const _avatarGradients = [
  ['#6366f1', '#8b5cf6'],
  ['#0ea5e9', '#2563eb'],
  ['#f59e0b', '#d97706'],
  ['#10b981', '#059669'],
  ['#ef4444', '#dc2626'],
  ['#ec4899', '#db2777'],
  ['#14b8a6', '#0d9488'],
  ['#8b5cf6', '#7c3aed'],
  ['#f97316', '#ea580c'],
  ['#06b6d4', '#0891b2'],
];

function avatarColor(name) {
  let hash = 0;
  const str = String(name || '');
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const [c0, c1] = _avatarGradients[Math.abs(hash) % _avatarGradients.length];
  return `linear-gradient(135deg, ${c0} 0%, ${c1} 100%)`;
}

function mergeNotifications(existing, incoming) {
  return NotificationService.mergeNotificationLists(existing, incoming);
}

function fmt12h(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  let hours = parseInt(h, 10);
  const mod = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${m} ${mod}`;
}

/** Build a single notification for today's unmarked-attendance sessions. */
function buildUnmarkedAttendanceNotification(schedules) {
  if (!schedules || schedules.length === 0) return [];

  const courseCounts = {};
  schedules.forEach((s) => {
    const k = s.courseName || 'Uncategorized';
    courseCounts[k] = (courseCounts[k] || 0) + 1;
  });

  const courseSummary = Object.entries(courseCounts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([course, count]) => `${course}: ${count}`)
    .join(' · ');

  return [
    {
      id: 'unmarked-attendance-summary',
      type: 'warning',
      title: `🔴 ${schedules.length} class${schedules.length > 1 ? 'es' : ''} with unmarked attendance today`,
      message: `Attendance was not recorded for ended sessions. ${courseSummary}`,
      link: '/admin/notifications/unmarked-attendance',
      isRead: false,
      createdAt: new Date().toISOString(),
    },
  ];
}

/** Build local notification objects from stale ticket data. */
function buildStaleNotifications(overdueOpen, overdueInProgress) {
  const totalOpen = overdueOpen.length;
  const totalInProgress = overdueInProgress.length;
  const total = totalOpen + totalInProgress;

  if (total === 0) return [];

  // Build a compact course-wise summary line
  const courseCounts = {};
  overdueOpen.forEach((t) => {
    const k = t.courseName || 'Uncategorized';
    if (!courseCounts[k]) courseCounts[k] = { open: 0, inProgress: 0 };
    courseCounts[k].open += 1;
  });
  overdueInProgress.forEach((t) => {
    const k = t.courseName || 'Uncategorized';
    if (!courseCounts[k]) courseCounts[k] = { open: 0, inProgress: 0 };
    courseCounts[k].inProgress += 1;
  });

  const courseSummary = Object.entries(courseCounts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([course, { open, inProgress }]) => {
      const parts = [];
      if (open > 0) parts.push(`${open} open`);
      if (inProgress > 0) parts.push(`${inProgress} in-progress`);
      return `${course}: ${parts.join(', ')}`;
    })
    .join(' · ');

  const parts = [];
  if (totalOpen > 0) parts.push(`${totalOpen} open >3 days`);
  if (totalInProgress > 0) parts.push(`${totalInProgress} in-progress >7 days`);

  return [
    {
      id: 'stale-issues-summary',
      type: 'warning',
      title: `⚠️ ${total} overdue student issue${total > 1 ? 's' : ''} require attention`,
      message: `${parts.join(' · ')}. ${courseSummary}`,
      link: '/admin/notifications/stale-issues',
      isRead: false,
      createdAt: new Date().toISOString(),
    },
  ];
}

export default function TopBar({ title, onMenuClick, sidebarOpen, showStaffPresenceButton = false }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [teamworkCount, setTeamworkCount] = useState(0);
  const [teamworkOverdue, setTeamworkOverdue] = useState(false);
  const [showStaffPresenceModal, setShowStaffPresenceModal] = useState(false);
  const [presenceLoading, setPresenceLoading] = useState(false);
  const [presenceError, setPresenceError] = useState('');
  const [presenceStaffList, setPresenceStaffList] = useState([]);
  const [presenceOnlineStaffMap, setPresenceOnlineStaffMap] = useState({});
  const [presenceOnlineSuperAdminNameMap, setPresenceOnlineSuperAdminNameMap] = useState({});
  const [presenceSearch, setPresenceSearch] = useState('');
  const staleNotifIdsRef = useRef(new Set());
  const unmarkedNotifIdRef = useRef(false); // true when the notif is injected
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const loadTeamworkCount = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/staff-teamwork/count').catch(() => null);
      if (res?.data?.count !== undefined) {
        setTeamworkCount(res.data.count);
        setTeamworkOverdue((res.data.overdueCount || 0) > 0);
      }
    } catch {}
  }, []);

  // Polling fallback
  useEffect(() => {
    loadTeamworkCount();
    const interval = setInterval(loadTeamworkCount, 60000);
    return () => clearInterval(interval);
  }, [loadTeamworkCount]);

  // Live count – same-tab (CustomEvent) + cross-tab (storage)
  useEffect(() => {
    window.addEventListener('ka-teamwork-count-changed', loadTeamworkCount);
    const storageHandler = (e) => { if (e.key === 'ka_tw_refresh') loadTeamworkCount(); };
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('ka-teamwork-count-changed', loadTeamworkCount);
      window.removeEventListener('storage', storageHandler);
    };
  }, [loadTeamworkCount]);

  const stored = AuthService.getUser();
  const userId = stored?.superAdmin?.superAdminId;
  const staff = stored?.superAdmin?.staff;
  const currentStaffId = staff?.staffId || null;
  const currentSuperAdminId = stored?.superAdmin?.superAdminId || null;
  const staffRoleRaw = staff?.staffType || staff?.role || staff?.position || staff?.designation || staff?.type || '';
  const normalizedRole = String(staffRoleRaw).trim().toLowerCase();
  const isSuperAdmin = Boolean(stored?.superAdmin?.superAdminId);
  const isAdminRole = normalizedRole === 'admin' || normalizedRole === 'administrator';
  const isManagementRole = normalizedRole === 'management' || normalizedRole === 'manager';
  const canViewSalesAdmin = isSuperAdmin || isAdminRole;
  const canViewRecovery = isSuperAdmin || isAdminRole || isManagementRole;
  const teamworkSocketUserId = stored?.superAdmin?.superAdminId || stored?.superAdmin?.staff?.staffId;
  const canOpenStaffPresence = Boolean(showStaffPresenceButton && isSuperAdmin);
  const currentPresenceName = stored?.superAdmin?.name || stored?.superAdmin?.staff?.name || '';

  const buildPresenceMaps = useCallback(({ onlineStaffIds, onlineSuperAdminNames } = {}) => {
    const nextStaff = {};
    if (Array.isArray(onlineStaffIds)) {
      onlineStaffIds.forEach((id) => {
        nextStaff[String(id)] = true;
      });
    }
    if (currentStaffId) {
      nextStaff[String(currentStaffId)] = true;
    }

    const nextNames = {};
    if (Array.isArray(onlineSuperAdminNames)) {
      onlineSuperAdminNames.forEach((name) => {
        const normalized = String(name || '').trim().toLowerCase();
        if (normalized) nextNames[normalized] = true;
      });
    }

    if (currentPresenceName) {
      const selfKey = String(currentPresenceName).trim().toLowerCase();
      if (selfKey) nextNames[selfKey] = true;
    }

    return { nextStaff, nextNames };
  }, [currentPresenceName, currentStaffId]);

  const applyPresenceSnapshot = useCallback((payload = {}) => {
    const { nextStaff, nextNames } = buildPresenceMaps(payload);
    setPresenceOnlineStaffMap(nextStaff);
    setPresenceOnlineSuperAdminNameMap(nextNames);
  }, [buildPresenceMaps]);

  const loadPresenceStaffList = useCallback(async () => {
    const res = await axiosInstance.get('/admin/staff');
    const list = res?.data?.staff || res?.data?.staffMembers || res?.data || [];
    const active = (Array.isArray(list) ? list : []).filter((member) => member.loginStatus !== 'blocked');
    setPresenceStaffList(active);
  }, []);

  const loadPresenceSnapshot = useCallback(async () => {
    const res = await axiosInstance.get('/admin/staff-teamwork/presence', {
      params: { _: Date.now() },
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    applyPresenceSnapshot(res?.data || {});
  }, [applyPresenceSnapshot]);

  const loadStaffPresence = useCallback(async ({ includeStaffList = presenceStaffList.length === 0 } = {}) => {
    if (!canOpenStaffPresence) return;

    if (includeStaffList) {
      setPresenceLoading(true);
    }
    setPresenceError('');

    try {
      if (includeStaffList) {
        await loadPresenceStaffList();
      }
      await loadPresenceSnapshot();
    } catch (err) {
      setPresenceError(err?.response?.data?.error || 'Failed to load staff presence.');
    } finally {
      if (includeStaffList) {
        setPresenceLoading(false);
      }
    }
  }, [canOpenStaffPresence, loadPresenceSnapshot, loadPresenceStaffList, presenceStaffList.length]);

  useEffect(() => {
    if (!canOpenStaffPresence) {
      setShowStaffPresenceModal(false);
      return undefined;
    }

    loadStaffPresence({ includeStaffList: true });
    const timer = setInterval(() => {
      loadStaffPresence({ includeStaffList: false });
    }, 30000);

    return () => clearInterval(timer);
  }, [canOpenStaffPresence, loadStaffPresence]);

  useEffect(() => {
    if (!showStaffPresenceModal) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowStaffPresenceModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showStaffPresenceModal]);

  const isPresenceStaffOnline = useCallback((member) => {
    const staffId = member?.staffId || member?.staff_id || member?.id;
    const onlineById = staffId !== undefined && staffId !== null
      ? Boolean(presenceOnlineStaffMap[String(staffId)])
      : false;
    if (onlineById) return true;

    const nameKey = String(member?.name || '').trim().toLowerCase();
    if (!nameKey) return false;
    return Boolean(presenceOnlineSuperAdminNameMap[nameKey]);
  }, [presenceOnlineStaffMap, presenceOnlineSuperAdminNameMap]);

  const onlinePresenceStaff = presenceStaffList.filter((member) => isPresenceStaffOnline(member));
  const offlinePresenceStaff = presenceStaffList.filter((member) => !isPresenceStaffOnline(member));

  const getPresenceStaffLabel = useCallback((member) => (
    member?.position || member?.type || member?.staffType || member?.role || '—'
  ), []);

  const _presenceSearchLower = presenceSearch.trim().toLowerCase();
  const filteredOnlineStaff = _presenceSearchLower
    ? onlinePresenceStaff.filter((m) => String(m.name || '').toLowerCase().includes(_presenceSearchLower))
    : onlinePresenceStaff;
  const filteredOfflineStaff = _presenceSearchLower
    ? offlinePresenceStaff.filter((m) => String(m.name || '').toLowerCase().includes(_presenceSearchLower))
    : offlinePresenceStaff;

  // ── Live notifications (admin only) ────────────────────────────────────────
  const loadNotifications = useCallback(async () => {
    if (!userId || !isAdminRole) return;
    setNotifLoading(true);
    try {
      const data = await NotificationService.fetchNotifications(userId);
      setNotifications((prev) => mergeNotifications(prev, data));
    } finally {
      setNotifLoading(false);
    }
  }, [userId, isAdminRole]);

  useEffect(() => {
    if (!userId || !isAdminRole) return;
    const cached = NotificationService.readLocalNotifications(userId);
    if (cached.length > 0) {
      setNotifications((prev) => mergeNotifications(prev, cached));
    }
  }, [userId, isAdminRole]);

  useEffect(() => {
    const handler = (event) => {
      const notif = event.detail;
      if (!notif || !userId || !isAdminRole) return;
      NotificationService.saveLocalNotification(userId, notif);
      loadNotifications();
    };

    window.addEventListener('kaaryaalaya:notif-local', handler);
    return () => window.removeEventListener('kaaryaalaya:notif-local', handler);
  }, [userId, isAdminRole, loadNotifications]);

  // Re-fetch when another component (e.g. Timetable.jsx) signals a new notification
  useEffect(() => {
    if (!isAdminRole) return;
    const handler = () => loadNotifications();
    window.addEventListener('kaaryaalaya:notif-refresh', handler);
    return () => window.removeEventListener('kaaryaalaya:notif-refresh', handler);
  }, [isAdminRole, loadNotifications]);

  // Poll every 30 s as a Socket.IO fallback (catches notifications from any source)
  useEffect(() => {
    if (!userId || !isAdminRole) return;
    const timer = setInterval(loadNotifications, 30_000);
    return () => clearInterval(timer);
  }, [userId, isAdminRole, loadNotifications]);

  useEffect(() => {
    if (!teamworkSocketUserId && (!userId || !isAdminRole)) return;
    NotificationService.connect(
      teamworkSocketUserId || userId,
      isSuperAdmin ? 'super_admin' : 'staff',
      stored?.superAdmin?.name || stored?.superAdmin?.staff?.name || '',
      stored?.superAdmin?.staff?.staffId || null
    );
    if (userId && isAdminRole) {
      loadNotifications();
      prepareBrowserNotifications();
      subscribePush(teamworkSocketUserId || userId, 'admin');
    }

    const offCount = NotificationService.onTeamworkCountUpdated(loadTeamworkCount);
    const offTeamworkAlert = NotificationService.onTeamworkMessageAlert((payload = {}) => {
      loadTeamworkCount();

      const isSelfSender =
        (payload?.senderType === 'staff' && Number(payload?.senderStaffId) === Number(currentStaffId)) ||
        (payload?.senderType === 'super_admin' && Number(payload?.senderSuperAdminId) === Number(currentSuperAdminId));
      if (isSelfSender) return;

      const title = payload?.title || (payload?.teamworkCode
        ? `${payload.teamworkCode} - New Teamwork Message`
        : 'New Teamwork Message');
      const body = payload?.body || payload?.messagePreview || 'You received a new teamwork message.';
      window.dispatchEvent(new CustomEvent('ka-teamwork-live-alert', {
        detail: {
          title,
          body,
          link: payload?.teamworkId ? `/admin/teamwork?teamworkId=${encodeURIComponent(payload.teamworkId)}` : '/admin/teamwork',
        },
      }));

      showTeamworkBrowserNotification(payload);
    });
    const offNew = NotificationService.onNewNotification((notif) => {
      setNotifications((prev) => mergeNotifications(prev, [notif]));
    });
    const offRead = NotificationService.onNotificationRead(({ id }) => {
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
    });
    const offAll = NotificationService.onAllRead(() => {
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    });
    const offDel = NotificationService.onNotificationDeleted(({ id }) => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    });

    return () => {
      offCount(); offTeamworkAlert(); offNew(); offRead(); offAll(); offDel();
      NotificationService.disconnect();
    };
  }, [teamworkSocketUserId, userId, isAdminRole, loadNotifications, loadTeamworkCount, currentStaffId, currentSuperAdminId, isSuperAdmin]);

  // ── Stale-issue polling ──────────────────────────────────────────────────────
  const checkStaleIssues = useCallback(async () => {
    if (!isAdminRole) return;
    try {
      const resp = await axiosInstance.get('/tickets/stale');
      const { overdueOpen = [], overdueInProgress = [] } = resp?.data || {};
      const built = buildStaleNotifications(overdueOpen, overdueInProgress);

      const builtIds = new Set(built.map((n) => n.id));
      const prevIds = staleNotifIdsRef.current;

      setNotifications((prev) => {
        let next = prev.filter((n) => !prevIds.has(n.id) || builtIds.has(n.id));
        built.forEach((notif) => {
          const existing = next.find((n) => n.id === notif.id);
          if (!existing) {
            next = [notif, ...next];
          }
        });
        return next.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      });
      staleNotifIdsRef.current = builtIds;
    } catch (_) {
      // best-effort
    }
  }, [isAdminRole]);

  useEffect(() => {
    if (!isAdminRole) return;
    checkStaleIssues();
    const timer = setInterval(checkStaleIssues, 5 * 60_000);
    return () => clearInterval(timer);
  }, [isAdminRole, checkStaleIssues]);

  // ── Unmarked-attendance polling ───────────────────────────────────────────
  const checkUnmarkedAttendance = useCallback(async () => {
    if (!isAdminRole) return;
    try {
      const resp = await axiosInstance.get('/admin/attendance/unmarked-today');
      const schedules = Array.isArray(resp?.data) ? resp.data : [];
      const built = buildUnmarkedAttendanceNotification(schedules);
      const hasNew = built.length > 0;

      setNotifications((prev) => {
        // Remove old unmarked notif if classes are now all marked
        let next = prev.filter((n) => n.id !== 'unmarked-attendance-summary');
        if (hasNew) {
          const existing = prev.find((n) => n.id === 'unmarked-attendance-summary');
          // Preserve isRead state if already acknowledged
          next = [{ ...built[0], isRead: existing?.isRead ?? false }, ...next];
        }
        return next.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      });
      unmarkedNotifIdRef.current = hasNew;
    } catch (_) {
      // best-effort
    }
  }, [isAdminRole]);

  useEffect(() => {
    if (!isAdminRole) return;
    checkUnmarkedAttendance();
    const timer = setInterval(checkUnmarkedAttendance, 10 * 60_000); // every 10 min
    return () => clearInterval(timer);
  }, [isAdminRole, checkUnmarkedAttendance]);
  // ────────────────────────────────────────────────────────────────────────────

  const handleMarkAllRead = async () => {
    if (!userId) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try { await NotificationService.markAllRead(userId); } catch (_) {}
  };

  const handleNotifClick = async (notif) => {
    if (!notif.isRead) {
      setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, isRead: true } : n));
      try { await NotificationService.markRead(notif.id, userId); } catch (_) {}
    }
    if (notif.link) window.location.href = notif.link;
  };
  // ────────────────────────────────────────────────────────────────────────────

  const getUserInfo = () => {
    if (!stored || !stored.superAdmin) {
      return { name: 'Guest User', email: 'guest@example.com', role: 'Guest', avatar: undefined };
    }
    const sa = stored.superAdmin;
    const staff = sa.staff;
    if (staff) {
      return {
        name: staff.name || 'Admin User',
        email: staff.officeEmail || staff.gmail || '',
        role: canViewSalesAdmin ? 'Admin' : (staff.type || 'Staff'),
        // use stored imagePath directly in img src
        avatar: staff.imagePath || undefined,
      };
    }
    return { name: 'User', email: '', role: 'User', avatar: undefined };
  };

  const user = getUserInfo();

  const handleSearch = (e) => {
    e.preventDefault();
    // placeholder search
    console.log('Searching for:', searchQuery);
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post('/logout');
    } catch {}
    AuthService.clear();
    window.location.href = '/';
  };

  const roleColors = {
    Admin: { bg: 'linear-gradient(90deg,#f3e8ff,#fce7f3)', color: '#6b21a8', border: '#d8b4fe' },
  };
  const rc = roleColors[user.role] || { bg: 'linear-gradient(90deg,#f3f4f6,#e2e8f0)', color: '#1f2937', border: '#cbd5e1' };

  return (
    <>
      <Header className={sidebarOpen ? 'ml-0' : 'ml-0'}>
      <Left>
        <IconButton onClick={onMenuClick} aria-label="Toggle menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
        </IconButton>
        {title && <h1 style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>{title}</h1>}
        <nav aria-label="Breadcrumb" style={{ display: 'none' }}></nav>
      </Left>
      <Right>
        <TeamworkLiveToast />
        {canOpenStaffPresence && (
          <div style={{ position: 'relative' }}>
            <IconButton
              type="button"
              onClick={() => {
                setShowStaffPresenceModal(true);
                setShowNotifications(false);
                setShowUserMenu(false);
                loadStaffPresence({ includeStaffList: presenceStaffList.length === 0 });
              }}
              title={`${onlinePresenceStaff.length} staff online — click to view`}
              aria-label="Online staff"
              style={{ position: 'relative', color: '#16a34a' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <PresenceLiveDot />
              {(!presenceLoading || presenceStaffList.length > 0) && (
                <PresenceCountBadge>{onlinePresenceStaff.length}</PresenceCountBadge>
              )}
            </IconButton>
          </div>
        )}
        {/* Teamwork Icon Button */}
        <TeamworkButton count={teamworkCount} overdue={teamworkOverdue} onClick={() => window.open('/admin/teamwork', '_blank')} />
        {/* Bell Notification Icon — admin only */}
        {isAdminRole && <div style={{ position: 'relative' }}>
          <IconButton
            onClick={() => {
              const opening = !showNotifications;
              setShowNotifications((v) => !v);
              setShowUserMenu(false);
              if (opening && unreadCount > 0) handleMarkAllRead();
            }}
            aria-label="Notifications"
            style={{ position: 'relative' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
            </svg>
            {unreadCount > 0 && <NotifBadge>{unreadCount}</NotifBadge>}
          </IconButton>
          {showNotifications && (
            <NotifDropdown>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Notifications</span>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    style={{ fontSize: 12, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Mark all as read
                  </button>
                )}
              </div>
              {/* Notification list */}
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {notifLoading ? (
                  <p style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading…</p>
                ) : notifications.length === 0 ? (
                  <p style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No notifications</p>
                ) : (() => {
                  const unread = notifications.filter((n) => !n.isRead);
                  const read = notifications.filter((n) => n.isRead);
                  const renderNotif = (notif) => (
                    <NotifItem key={notif.id} $unread={!notif.isRead} onClick={() => handleNotifClick(notif)}>
                      {/* Type icon + unread indicator */}
                      <div style={{ flexShrink: 0, marginTop: 1, position: 'relative' }}>
                        {notif.type === 'warning' ? (
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                          </div>
                        ) : (
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: !notif.isRead ? '#dcfce7' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: !notif.isRead ? '#22c55e' : '#d1d5db' }} />
                          </div>
                        )}
                        {/* Green dot badge for unread warning-type */}
                        {!notif.isRead && notif.type === 'warning' && (
                          <span style={{ position: 'absolute', top: -2, right: -2, width: 9, height: 9, borderRadius: '50%', background: '#22c55e', border: '1.5px solid #fff' }} />
                        )}
                      </div>
                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: !notif.isRead ? 700 : 500, color: '#111827', marginBottom: 3, lineHeight: 1.35 }}>{notif.title}</p>
                        <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.45, marginBottom: 6 }}>{notif.message}</p>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>{relativeTime(notif.createdAt)}</span>
                          {notif.link && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#6366f1', display: 'flex', alignItems: 'center', gap: 2 }}>
                              View details
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                            </span>
                          )}
                        </div>
                      </div>
                    </NotifItem>
                  );
                  return (
                    <>
                      {unread.length > 0 && (
                        <>
                          <div style={{ padding: '6px 16px 4px', display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block', flexShrink: 0 }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Unread · {unread.length}
                            </span>
                          </div>
                          {unread.map(renderNotif)}
                        </>
                      )}
                      {read.length > 0 && (
                        <>
                          <div style={{ padding: '6px 16px 4px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', borderTop: unread.length > 0 ? '2px solid #e5e7eb' : 'none' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Earlier
                            </span>
                          </div>
                          {read.map(renderNotif)}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              {/* See all */}
              <div style={{ borderTop: '1px solid #e5e7eb' }}>
                <button
                  onClick={() => { setShowNotifications(false); window.location.href = '/admin/notifications'; }}
                  style={{ width: '100%', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center', letterSpacing: 0.2 }}
                >
                  See all notifications
                </button>
              </div>
            </NotifDropdown>
          )}
        </div>}
        <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            <svg style={{ position: 'absolute', left: 12, top: 10 }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          </div>
        </form>
        <div style={{ position: 'relative' }}>
          <IconButton onClick={() => { setShowUserMenu((v) => !v); setShowNotifications(false); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px' }}>
            {user.avatar && (
              <img
                src={getImageURL(user.avatar)}
                alt={user.name || 'User'}
                style={{ height: 32, width: 32, borderRadius: '9999px', objectFit: 'cover' }}
              />
            )}
            <span style={{ marginLeft: 8, fontSize: 14, color: '#374151', fontWeight: 500 }}>{user.name || 'User'}</span>
            <svg style={{ marginLeft: 4 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
          </IconButton>
          {showUserMenu && (
            <Dropdown>
              <Row>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{user.name}</p>
                <p style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>{user.email}</p>
                <RoleBadge style={{ marginTop: 8, background: rc.bg, color: rc.color, borderColor: rc.border }}>{user.role}</RoleBadge>
              </Row>
              <div>
                <MenuBtn onClick={() => (window.location.href = '/admin/profile')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  Your Profile
                </MenuBtn>
                <MenuBtn onClick={() => (window.location.href = '/lecturer/dashboard')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                  Lecturer View
                </MenuBtn>
                {canViewSalesAdmin && (
                  <MenuBtn onClick={() => (window.location.href = '/admin-sales/dashboard')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                    View Sales Admin
                  </MenuBtn>
                )}
                {canViewRecovery && (
                  <MenuBtn onClick={() => (window.location.href = '/recovery/dashboard')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    View Due Recovery
                  </MenuBtn>
                )}
              </div>
              <div style={{ borderTop: '1px solid #e5e7eb' }}>
                <MenuBtn onClick={handleLogout} style={{ color: '#dc2626' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                  Sign Out
                </MenuBtn>
              </div>
            </Dropdown>
          )}
        </div>
      </Right>
      </Header>

      {showStaffPresenceModal && (
        <>
          <PresenceModalOverlay onClick={() => setShowStaffPresenceModal(false)} />
          <PresenceModal role="dialog" aria-modal="true" aria-labelledby="staff-presence-title">
            <PresenceModalHead>
              <PresenceModalHeadTop>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    {/* Icon */}
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <div id="staff-presence-title" style={{ fontSize: 15, fontWeight: 700, color: '#ffffff', letterSpacing: '-0.01em' }}>Staff Presence</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 1 }}>Real-time activity monitor</div>
                    </div>
                    <PresenceLiveBadge style={{ marginLeft: 4, background: 'rgba(34,197,94,0.18)', border: '1px solid rgba(34,197,94,0.40)', color: '#86efac' }}>LIVE</PresenceLiveBadge>
                  </div>
                </div>
                <PresenceHeaderActions>
                  <PresenceHeadButton
                    type="button"
                    title="Refresh"
                    onClick={() => loadStaffPresence({ includeStaffList: true })}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                    Refresh
                  </PresenceHeadButton>
                  <PresenceHeadButton
                    type="button"
                    title="Close (Esc)"
                    onClick={() => setShowStaffPresenceModal(false)}
                    style={{ background: 'rgba(239,68,68,0.18)', borderColor: 'rgba(239,68,68,0.35)', color: '#fca5a5' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </PresenceHeadButton>
                </PresenceHeaderActions>
              </PresenceModalHeadTop>
              <PresenceModalHeadStats>
                <PresenceStatPill $color="#86efac">
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', flexShrink: 0, display: 'inline-block' }} />
                  {onlinePresenceStaff.length} Online
                </PresenceStatPill>
                <PresenceStatPill>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', flexShrink: 0, display: 'inline-block' }} />
                  {offlinePresenceStaff.length} Offline
                </PresenceStatPill>
                <PresenceStatPill $color="rgba(255,255,255,0.55)">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197"/></svg>
                  {presenceStaffList.length} Total
                </PresenceStatPill>
              </PresenceModalHeadStats>
            </PresenceModalHead>

            {!presenceLoading && !presenceError && presenceStaffList.length > 0 && (
              <PresenceSearchStrip>
                <div style={{ position: 'relative' }}>
                  <svg
                    style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                    width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5"
                  >
                    <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
                  </svg>
                  <PresenceSearchInput
                    type="text"
                    placeholder="Search staff by name…"
                    value={presenceSearch}
                    onChange={(e) => setPresenceSearch(e.target.value)}
                  />
                </div>
              </PresenceSearchStrip>
            )}
            <PresenceModalBody>
              {presenceLoading && presenceStaffList.length === 0 ? (
                <PresenceEmpty>Loading staff presence…</PresenceEmpty>
              ) : presenceError ? (
                <PresenceEmpty>{presenceError}</PresenceEmpty>
              ) : (
                <>
                  <PresenceSection>
                    <PresenceSectionTitle>
                      <span>Online</span>
                      <PresenceSectionCount style={{ background: '#dcfce7', color: '#15803d' }}>
                        {filteredOnlineStaff.length}
                      </PresenceSectionCount>
                    </PresenceSectionTitle>
                    {filteredOnlineStaff.length === 0 ? (
                      <PresenceEmpty>{presenceSearch ? 'No match found' : 'No staff online right now'}</PresenceEmpty>
                    ) : (
                      filteredOnlineStaff.map((member) => (
                        <PresenceMemberRow $online key={`presence-online-${member.staffId || member.staff_id || member.id}`}>
                          <PresenceAvatar style={{ background: member.imagePath ? undefined : avatarColor(member.name) }}>
                            {member.imagePath ? <img src={getImageURL(member.imagePath)} alt={member.name} /> : initials(member.name)}
                            <PresenceLiveDot style={{ top: 'auto', left: 'auto', bottom: 1, right: 1 }} />
                          </PresenceAvatar>
                          <PresenceInfo style={{ flex: 1 }}>
                            <PresenceName>{member.name}</PresenceName>
                            <PresenceRole>{getPresenceStaffLabel(member)}</PresenceRole>
                          </PresenceInfo>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: '#dcfce7', color: '#15803d', flexShrink: 0 }}>Active</span>
                        </PresenceMemberRow>
                      ))
                    )}
                  </PresenceSection>

                  <PresenceSection>
                    <PresenceSectionTitle>
                      <span>Offline</span>
                      <PresenceSectionCount>{filteredOfflineStaff.length}</PresenceSectionCount>
                    </PresenceSectionTitle>
                    {filteredOfflineStaff.length === 0 ? (
                      <PresenceEmpty>{presenceSearch ? 'No match found' : 'All staff are online'}</PresenceEmpty>
                    ) : (
                      filteredOfflineStaff.map((member) => (
                        <PresenceMemberRow key={`presence-offline-${member.staffId || member.staff_id || member.id}`}>
                          <PresenceAvatar style={{ background: member.imagePath ? undefined : avatarColor(member.name), opacity: 0.72 }}>
                            {member.imagePath ? <img src={getImageURL(member.imagePath)} alt={member.name} style={{ opacity: 0.75 }} /> : initials(member.name)}
                          </PresenceAvatar>
                          <PresenceInfo style={{ flex: 1 }}>
                            <PresenceName style={{ color: '#475569' }}>{member.name}</PresenceName>
                            <PresenceRole>{getPresenceStaffLabel(member)}</PresenceRole>
                          </PresenceInfo>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 999, background: '#f1f5f9', color: '#94a3b8', flexShrink: 0 }}>Away</span>
                        </PresenceMemberRow>
                      ))
                    )}
                  </PresenceSection>
                </>
              )}
            </PresenceModalBody>
          </PresenceModal>
        </>
      )}
    </>
  );
}
